<template>
    <div class="resident-requests">
        <v-container>
            <v-row>
                <v-col class="mb-4 pb-0">
                    <p class="greetings text-center mb-0">קריאות תושבים</p>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" sm="4" class="pb-0">
                    <v-text-field
                        v-model="pickupFilter"
                        label="סינון לפי מספר פינוי"
                        inputmode="numeric"
                        clearable
                        outlined
                        dense
                        hide-details
                        @keypress="onlyNumbers"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4" class="pb-0">
                    <v-text-field
                        v-model="cityFilter"
                        label="סינון לפי שם העיר"
                        clearable
                        outlined
                        dense
                        hide-details></v-text-field>
                </v-col>
                <v-col cols="12" sm="4" class="pb-0">
                    <v-text-field
                        v-model="requestDateFilter"
                        type="date"
                        label="סינון לפי תאריך יעד"
                        clearable
                        outlined
                        dense
                        hide-details></v-text-field>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <template v-if="residentRequestsLoading">
                        <div class="loaderSection" v-for="index in 4" :key="'rr-skeleton-' + index">
                            <v-skeleton-loader type="list-item-avatar, article"></v-skeleton-loader>
                        </div>
                    </template>
                    <template v-else>
                        <div v-for="group in groupedResidentRequests"
                             :key="group.pickupNumber"
                             class="pickup-group mb-6">
                            <div class="pickup-header mb-2">
                                פינוי {{ group.pickupNumber }}
                                <span v-if="group.date" class="pickup-date">· {{ formatDate(group.date) }}</span>
                                <span class="pickup-count">({{ group.items.length }})</span>
                            </div>
                            <v-resident-request-card
                                v-for="req in group.items"
                                :key="req.id"
                                :request="req"
                                @submitted="onRequestSubmitted"></v-resident-request-card>
                        </div>
                        <p class="text-center" v-if="residentRequestsLoaded && !groupedResidentRequests.length">
                            אין קריאות תושבים ממתינות
                        </p>
                    </template>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
    import {mapState, mapActions} from 'vuex';
    import vResidentRequestCard from '../components/vResidentRequestCard'
    import moment from 'moment'

    export default {
        name: 'vResidentRequests',
        components: {
            vResidentRequestCard,
        },
        data() {
            return {
                requestDateFilter: null,
                pickupFilter: '',
                cityFilter: '',
            }
        },
        watch: {
            /* keep the pickup-number filter digits-only, incl. paste */
            pickupFilter(val) {
                if (val && /\D/.test(val)) {
                    this.pickupFilter = val.replace(/\D/g, '');
                }
            },
        },
        mounted() {
            if (!this.residentRequestsLoaded && !this.residentRequestsLoading) {
                this.GET_RESIDENT_REQUESTS();
            }
        },
        computed: {
            ...mapState(['residentRequests', 'residentRequestsLoading', 'residentRequestsLoaded']),
            /* group waiting resident requests by their parent pickup, honoring the filters.
               Ordering: groups by date then מספר פינוי; within each pickup, residents are
               ordered by city so the same city sits together (date → מספר פינוי → city). */
            groupedResidentRequests() {
                const dateFilter = this.requestDateFilter;
                const pickupFilter = this.pickupFilter;
                const cityFilter = (this.cityFilter || '').trim().toLowerCase();
                const list = (this.residentRequests || []).filter(r => {
                    if (dateFilter && r.dueDate !== dateFilter) return false;
                    if (pickupFilter && !String(r.pickupNumber || '').includes(pickupFilter)) return false;
                    if (cityFilter && !String(r.city || '').toLowerCase().includes(cityFilter)) return false;
                    return true;
                });
                const groups = [];
                const byPickup = {};
                for (const r of list) {
                    const key = r.pickupNumber || r.pickupId || '—';
                    if (!byPickup[key]) {
                        byPickup[key] = { pickupNumber: key, date: r.dueDate, items: [] };
                        groups.push(byPickup[key]);
                    }
                    const g = byPickup[key];
                    g.items.push(r);
                    /* use the earliest due date in the pickup as the group's date */
                    if (r.dueDate && (!g.date || r.dueDate < g.date)) g.date = r.dueDate;
                }
                /* within each pickup, keep residents of the same city together (sorted by city) */
                for (const g of groups) {
                    g.items.sort((a, b) => {
                        const ca = (a.city || '').trim();
                        const cb = (b.city || '').trim();
                        if (!ca && !cb) return 0;
                        if (!ca) return 1;
                        if (!cb) return -1;
                        return ca.localeCompare(cb, 'he');
                    });
                }
                /* order the groups by date, then by מספר פינוי */
                groups.sort((a, b) => {
                    const da = a.date || '';
                    const db = b.date || '';
                    if (da !== db) return da < db ? -1 : 1;
                    return String(a.pickupNumber).localeCompare(String(b.pickupNumber), undefined, {numeric: true});
                });
                return groups;
            },
        },
        methods: {
            ...mapActions(['GET_RESIDENT_REQUESTS']),
            /* block any non-digit keypress in the pickup-number filter */
            onlyNumbers(e) {
                if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                }
            },
            formatDate(date) {
                return date ? moment(date).format('DD/MM/YYYY') : '';
            },
            onRequestSubmitted() {
                /* a request changed status server-side; refresh the list */
                this.GET_RESIDENT_REQUESTS();
            },
        },
    }
</script>

<style lang="scss" scoped>
    .resident-requests {
        padding-top: 70px;
    }

    .greetings {
        font-size: 22px;
        font-weight: 500;
    }

    .pickup-group {
        .pickup-header {
            font-size: 15px;
            font-weight: 700;
            color: #333;
            border-bottom: 1px solid #e0e0e0;
            padding-bottom: 4px;

            .pickup-date {
                font-weight: 400;
                color: #777;
            }

            .pickup-count {
                font-weight: 400;
                color: #999;
                font-size: 13px;
            }
        }
    }
</style>
